// SEQ-216: loads what OTHER claims already claimed of a project's hire, for the
// claim preview. Kept out of the modal so a failure and a stale response (the
// user changed month while the request was in flight) are testable with a
// delayed fake client.
import { readReclaimOrigins } from "./hireClaimability";
import {
  buildHireClaimedElsewhereFilter,
  isTruncatedPage,
  resolveHireOwnerIds,
  type AttachedHireRow,
  type PriorReportHireInput,
} from "./claimSummary";

interface SupabaseLikeClient {
  // Matches the supabase-js query-builder surface this module uses.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  from(table: string): any;
}

export interface PriorReportWithNumber extends PriorReportHireInput {
  claim_number?: number | null;
}

export interface PriorHireData {
  reports: PriorReportWithNumber[];
  rows: AttachedHireRow[];
  currentReportId: string | null;
  /**
   * SEQ-218: rows the claim being regenerated froze as re-claims (tuple key to
   * the report they came from). Empty for a new claim.
   */
  reclaimOrigins: Map<string, string>;
}

/**
 * Resolves the hire data for one claim period, or null when isCancelled()
 * turned true while a request was in flight (a newer month owns the state).
 * Throws the database error on any failed query: the caller shows a retry
 * state instead of a total that silently omits earlier claims.
 */
export async function loadHireClaimedElsewhere(
  client: SupabaseLikeClient,
  args: {
    projectId: string;
    year: number;
    month: number;
    isCancelled: () => boolean;
  },
): Promise<PriorHireData | null> {
  const { projectId, year, month, isCancelled } = args;
  const [reportsRes, currentRes] = await Promise.all([
    client
      .from("claims_reports")
      .select("id, claim_year, claim_month, claim_number")
      .eq("project_id", projectId),
    client
      .from("claims_reports")
      .select("id, claimed_set")
      .eq("project_id", projectId)
      .eq("claim_month", month)
      .eq("claim_year", year)
      .maybeSingle(),
  ]);
  if (reportsRes.error) throw reportsRes.error;
  if (currentRes.error) throw currentRes.error;
  if (isCancelled()) return null;

  const reports = (reportsRes.data || []) as PriorReportWithNumber[];
  const currentReportId: string | null = currentRes.data?.id ?? null;
  const ownerIds = resolveHireOwnerIds({
    reports,
    claimYear: year,
    claimMonth: month,
    existingReportId: currentReportId,
  });

  const rowsRes = await client
    .from("claims_hire_billing")
    .select("id, hire_register_id, claim_report_id, paid, billing_month, billing_year, amount", {
      count: "exact",
    })
    .eq("project_id", projectId)
    .or(buildHireClaimedElsewhereFilter(ownerIds));
  if (rowsRes.error) throw rowsRes.error;
  // max_rows = 1000: a truncated page would silently omit claimed hire from A.
  if (isTruncatedPage(rowsRes.count, rowsRes.data)) {
    throw new Error("Earlier hire claims were truncated");
  }
  if (isCancelled()) return null;

  return {
    reports,
    rows: (rowsRes.data || []) as AttachedHireRow[],
    currentReportId,
    reclaimOrigins: readReclaimOrigins(currentRes.data?.claimed_set, {
      reports,
      claimYear: year,
      claimMonth: month,
    }),
  };
}

/**
 * Identifies the period (and existing report) a preview figure was computed
 * for. The modal compares the stamp on its data with the stamp of the period
 * on screen, so one frame of last month's rows after a month switch (or after
 * the existing report id changes) can never read as a ready preview.
 */
export function periodStamp(
  year: number,
  month: number,
  reportId: string | null | undefined,
): string {
  return `${year}-${month}:${reportId ?? "none"}`;
}
